'use client';

import * as React from 'react';
import {
	buildConsumptionTrend,
	buildTimeBandBreakdown,
	calcEstimatedTotals,
	type ConsumptionTrendInput,
	type Fascia,
	fmtNum,
	fmtPct,
} from '@slims/shared/calculations';
import { TIME_BANDS, IT_INT } from '@slims/shared/constants';
import type { CERConfig, EnergyMember } from '@slims/shared/types';

import { Row, Stack, Text } from '../../atoms';
import { SlChart, TimeBandBar } from '../../charts';
import {
	AiBox,
	BarsSkeleton,
	BodySkeleton,
	CalcTrace,
	type CalcTraceData,
	CardGrid,
	ChartSkeleton,
	DashGrid,
	DataTable,
	type DataTableColumn,
	Footnote,
	InfoStrip,
	KeyValueListSkeleton,
	KpiBig,
	KpiSkeleton,
	ListSkeleton,
	MiniBar,
	PanelCard,
	PanelCardSkeleton,
	ProgressItem,
	SegmentedTabs,
	StatusBlock,
	StimaInfo,
	Tag,
	TONE_VAR,
	type WidgetTone,
} from '../../molecules';
import { buildPeriodTabs, monthName, PERIOD_CURRENT, periodMonth } from '../../lib/period-tabs';
import { PageTitle } from '../../templates';

// ─── Produzione ──────────────────────────────────────────────────────────────

export type MonitoringPlantStato = 'ok' | 'warn' | 'crit';
export type MonitoringPlantTipo = 'ci' | 'campo_terra' | 'tetto';

export interface MonitoringPlantCard {
	id: string;
	nome: string;
	localita: string | null;
	tipo: MonitoringPlantTipo | null;
	potenzaKwp: number;
	liveKw: number;
	pr: number | null;
	prTarget: number | null;
	inverterTotali: number;
	inverterOk: number;
	stringheTotali: number;
	anomalieAperte: number;
	stato: MonitoringPlantStato;
	/**
	 * Almeno un inverter ha mandato un campione negli ultimi minuti. `false`:
	 * l'impianto non si mostra «LIVE» anche se nessuno lo segnala in allerta
	 * (INB-333). Assente = come prima.
	 */
	inLinea?: boolean;
	energiaGiornoKwh: number | null;
	/** Parte stimata di `energiaGiornoKwh` (INB-354). */
	energiaGiornoStimataKwh?: number;
}

export interface MonitoringPortfolioKpi {
	impiantiTotali: number;
	impiantiOnline: number;
	potenzaTotaleKwp: number;
	inverterMonitorati: number;
	stringheMonitorate: number;
	prMedio: number | null;
	disponibilita: number | null;
	liveKw: number;
	energiaPeriodoKwh: number;
	/** Parte stimata di `energiaPeriodoKwh` (INB-354). */
	energiaPeriodoStimataKwh?: number;
	composizione: { tipo: MonitoringPlantTipo | null; count: number }[];
	/**
	 * Curva della giornata sommata su tutti gli impianti. `nominale` è la
	 * potenza attesa dall'irraggiamento reale della località — presente solo
	 * qui (curva di portfolio): il dettaglio di un singolo impianto usa
	 * `MonitoringPlantDetail.curva`, che non la porta.
	 */
	curvaOggi: { ora: string; ac: number; dc: number; nominale?: number }[];
	/** Giorno della curva live, sempre oggi (UTC), indipendente dal periodo dei KPI. */
	giornoCurva: string;
	/** Quota di energia immessa condivisa nel periodo — stessa formula del Dashboard. */
	selfConsumptionPct: number | null;
	/** Tempo medio di risoluzione delle anomalie chiuse nel periodo, in ore. */
	mttrMedioOre: number | null;
	/** Variazione % di `energiaPeriodoKwh` sul periodo precedente di pari durata. `null` senza confronto sensato. */
	energiaPeriodoDeltaPct: number | null;
}

export interface MonitoringPlantInverter {
	id: string;
	codice: string;
	potenzaKw: number | null;
	marca: string | null;
	modello: string | null;
	stringheTotali: number | null;
	stato: 'ok' | 'warn' | 'fault' | 'offline';
	ultimoContattoAt: string | null;
	liveKw: number;
	/** Performance Ratio del giorno richiesto (0-1). */
	pr: number | null;
}

export interface MonitoringPlantTypicalDaySeries {
	label: 'Picco' | 'Media' | 'Peggiore';
	giorno: string;
	punti: { ora: string; ac: number }[];
}

export interface MonitoringPlantDetail {
	plant: MonitoringPlantCard;
	inverters: MonitoringPlantInverter[];
	/** Curva DC/AC di oggi — solo sul mese in corso, vedi `curvaTipo`. */
	curva: { ora: string; ac: number; dc: number }[];
	/**
	 * Tre giornate tipo del mese richiesto (picco, media, peggiore energia),
	 * potenza AC stimata — non misurata, solo sui mesi passati (la telemetria
	 * minuto per minuto non copre un mese già chiuso). `null` sul mese in corso.
	 */
	curvaTipo: MonitoringPlantTypicalDaySeries[] | null;
	/** Giorno della curva DC/AC, sempre oggi (UTC). */
	giornoCurva: string;
	/** Giorno dei KPI, di `poaWm2` e `tModuliC`, `YYYY-MM-DD`. */
	giorno: string;
	mensile: { giorno: string; energiaKwh: number | null; pr: number | null }[];
	poaWm2: number | null;
	tModuliC: number | null;
}

export interface MonitoringPlantOption {
	id: string;
	nome: string;
	localita: string | null;
}

// ─── Consumi ─────────────────────────────────────────────────────────────────

export interface MonitoringMeterConsumption {
	meterId: string;
	codice: string;
	memberName: string | null;
	memberType: string | null;
	prelevata: number;
	/** Quota % sul totale condiviso della configurazione (mock: "% Cond."). */
	pctCondivisaConfig: number | null;
	/** % del consumo di questo POD coperta dalla condivisa attribuita al socio (mock: "AC"). */
	pctAutoconsumo: number | null;
}

export interface MonitoringEnergyTotals {
	totalImmessa: number;
	totalPrelevata: number;
	totalCondivisa: number;
}

/** Bersaglio dell'esploso per KPI, riportato al wiring che apre il dialogo. */
export interface MonitoringMetricTarget {
	metricId: string;
	kpiTotal?: number | null;
	periodo?: string;
	memberGroup?: 'res' | 'com' | 'pa';
	from?: string;
	to?: string;
}

/** Bersaglio dell'esploso per POD. */
export interface MonitoringPodTarget {
	meterId: string;
	puntoMisura: string;
	pod: string;
	direzione: string;
}

/**
 * La configurazione, come arriva dallo store del wiring: `id` e `tenantId`
 * mancano finché non è stata caricata, e la vista non li usa.
 */
export type MonitoringViewConfig = Omit<CERConfig, 'id' | 'tenantId'> & {
	id?: string;
	tenantId?: string;
};

/**
 * Vista attiva del Monitoraggio quando più configurazioni sono selezionate:
 * `multi` è la griglia «Panoramica» (una card per configurazione), `single` è
 * il monitoraggio completo di una configurazione sola. Stesso schema della
 * Dashboard.
 */
export type MonitoringViewMode = 'multi' | 'single';

/** Sezione del monitoraggio, indipendente dalla configurazione selezionata. */
export type MonitoringTab = 'produzione' | 'consumi';

/** Voce del picker di configurazione, in modalità singola. */
export interface MonitoringConfigPickerItem {
	id: string;
	cerNome: string;
	configNome: string;
}

export interface MonitoringViewProps {
	anomaliesEnabled?: boolean;
	// Produzione
	plants?: MonitoringPlantCard[];
	portfolio?: MonitoringPortfolioKpi | null;
	plantOptions?: MonitoringPlantOption[];
	plantDetail?: MonitoringPlantDetail | null;
	selectedPlantId?: string | null;
	onSelectPlant?: (id: string | null) => void;
	// Consumi
	config?: MonitoringViewConfig;
	members?: EnergyMember[];
	sharedEnergy?: ConsumptionTrendInput[];
	energyTotals: MonitoringEnergyTotals;
	meterConsumption?: MonitoringMeterConsumption[];
	onExplodeMetric?: (target: MonitoringMetricTarget) => void;
	onExplodePod?: (target: MonitoringPodTarget) => void;
	/** Sezione controllata dal wiring, per conservarla quando cambia configurazione. */
	tab?: MonitoringTab;
	onTabChange?: (tab: MonitoringTab) => void;
	/** Sezione iniziale quando `tab` non è controllata. Default: `produzione`. */
	initialTab?: MonitoringTab;
	/**
	 * Mese guardato da entrambi i pannelli, `YYYY-MM`; `null` è il mese in corso
	 * fino a oggi.
	 *
	 * La finestra la sceglie la vista ma il dato lo rifà il cablaggio — produzione
	 * e consumi cambiano interrogazione — quindi vive qui invece che nello stato
	 * interno. Il mock tiene due contatori separati (`__prodPer` e `__consPer`);
	 * qui è uno solo, perché è la stessa pagina e passare da Produzione a Consumi
	 * cambiando mese sotto i piedi non aiuta nessuno.
	 */
	period?: string | null;
	onPeriodChange?: (month: string | null) => void;
	/**
	 * Vista attiva. Assente (o `configs` con un solo elemento): niente toggle,
	 * si mostra solo il monitoraggio della configurazione — il comportamento di
	 * prima della «Panoramica».
	 */
	view?: MonitoringViewMode;
	onViewChange?: (view: MonitoringViewMode) => void;
	/** Configurazioni disponibili per il picker di modalità singola. */
	configs?: MonitoringConfigPickerItem[];
	/** Configurazione scelta nel picker, in modalità singola. */
	selectedConfigId?: string | null;
	onSelectConfig?: (id: string) => void;
	/**
	 * Contenuto della griglia «Panoramica»: una {@link MonitoringConfigCard} per
	 * configurazione, fornita dal wiring — ognuna fa il proprio fetch, la vista
	 * non sa come si ottiene.
	 */
	overviewGridSlot?: React.ReactNode;
	/**
	 * Stato di caricamento per sezione: ognuna dipende da una fetch diversa e
	 * mostra lo skeleton finché quella specifica non è arrivata. Assente/`false`
	 * equivale a «dato pronto».
	 */
	loading?: MonitoringViewLoading;
}

/** Vedi {@link MonitoringViewProps.loading}. */
export interface MonitoringViewLoading {
	/** Panoramica impianti: 5 KPI, elenco impianti, grafico + KPI portfolio. Dipende da `plants`/`portfolio`. */
	production?: boolean;
	/** Dettaglio del singolo impianto selezionato. Dipende da `plantDetail`. */
	plantDetail?: boolean;
	/** Scheda Consumi: dipende da `sharedEnergy`/`energyTotals`/`meterConsumption`/`members`. */
	consumption?: boolean;
}

const BAND_TONE: Record<Fascia, 'warn' | 'accent' | 'success'> = {
	F1: 'warn',
	F2: 'accent',
	F3: 'success',
};

// Etichette compatte come nel mock ("F1 - Punta (lun-ven 8–19)"), non la
// descrizione normativa estesa di `TIME_BANDS[fascia].description`
// ("Lun-Ven 8:00-19:00 (esclusi festivi)") — quella resta la fonte usata per
// i calcoli reali altrove, qui è solo il fraseggio di questa singola riga.
const FASCIA_LABEL_MOCK: Record<Fascia, string> = {
	F1: 'F1 - Punta (lun-ven 8–19)',
	F2: 'F2 - Intermedia (7–8, 19–23 lv; 7–23 sa)',
	F3: 'F3 - Fuori punta (notte, domenica)',
};

const COMMERCIAL_TYPES = new Set(['sme', 'office', 'shop', 'factory']);
const PA_TYPES = new Set(['public_administration', 'ets']);

type UserGroup = 'res' | 'com' | 'pa';

function groupOf(tipo: string | null | undefined): UserGroup {
	if (tipo && COMMERCIAL_TYPES.has(tipo)) return 'com';
	if (tipo && PA_TYPES.has(tipo)) return 'pa';
	return 'res';
}

function memberLabel(m: EnergyMember): string {
	return m.nome || `Socio ${m.id.slice(0, 8)}`;
}

const TIPO_LABEL: Record<MonitoringPlantTipo, string> = {
	ci: 'C&I',
	campo_terra: 'Campo a terra',
	tetto: 'Tetto',
};

/**
 * Colore del pallino di stato e dell'etichetta LIVE/ALLERTA/CRITICO — un solo
 * dizionario per entrambi, come `stColor()` nel mock (riusata identica per il
 * pallino e per il testo).
 *
 * `crit` è fisso (`#ef4444`), non `var(--color-danger)`: `stColor()` scrive
 * `'#ef4444'` come letterale per i critici (warning/ok restano temi-
 * dipendenti, `var(--warn)`/`var(--accent3)`) — `--color-danger` invece
 * scurisce in tema chiaro (`#dc2626`), disallineandosi dal mock proprio dove
 * lo stato è più grave. Stesso bug corretto in `anomalies-view.tsx`
 * (`SEVERITA_COLOR`), stessa funzione mock dietro entrambe le pagine.
 */
const STATO_DOT: Record<MonitoringPlantStato, string> = {
	ok: 'var(--color-accent-green)',
	warn: 'var(--color-warn)',
	crit: '#ef4444',
};

const STATO_LABEL: Record<MonitoringPlantStato, string> = {
	ok: 'LIVE',
	warn: 'ALLERTA',
	crit: 'CRITICO',
};

/** Colore ed etichetta dello stato come si mostra: un impianto «ok» che non manda dati non è «LIVE». */
function statoVisto(p: { stato: MonitoringPlantStato; inLinea?: boolean }): {
	color: string;
	label: string;
} {
	if (p.stato === 'ok' && p.inLinea === false)
		return { color: 'var(--color-text-muted)', label: 'NESSUN DATO' };
	return { color: STATO_DOT[p.stato], label: STATO_LABEL[p.stato] };
}

type ProdSubTab = 'panoramica' | 'impianto';

const PROD_TABS: { value: ProdSubTab; label: string }[] = [
	{ value: 'panoramica', label: 'Panoramica impianti' },
	{ value: 'impianto', label: 'Monitoraggio impianto' },
];

/** `31 maggio`, per intitolare col giorno vero invece che con «oggi». */
function dayLabel(iso: string): string {
	const d = new Date(`${iso}T00:00:00`);
	if (Number.isNaN(d.getTime())) return iso;
	return `${d.getDate()} ${monthName(d.getMonth())}`;
}

/** Oggi con la stessa regola del server (`toISOString`), o i due non coincidono. */
function todayIso(): string {
	return new Date().toISOString().slice(0, 10);
}

/**
 * Potenza istantanea in MW, come nel mock ("9.77 MW"): il database tiene le
 * potenze in kW (vedi la nota in `monitoring-view.fixtures.ts` — è la fonte,
 * non cambia), qui è solo la resa testuale. 2 decimali sempre, anche per un
 * impianto sotto 1 MW (il mock fa lo stesso: "0.71 MW", non "710 kW").
 */
function fmtMW(kw: number): string {
	return `${(kw / 1000).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MW`;
}

/**
 * Potenza installata: kWp sotto il megawatt, MWp da lì — decimali quanti
 * servono (1,8 non 1,80). «0,06 MWp» per un impianto che altrove è «60 kWp»
 * costringeva a convertire (INB-377).
 */
function fmtMWp(kwp: number): string {
	if (kwp < 1000) return `${kwp.toLocaleString('it-IT', { maximumFractionDigits: 1 })} kWp`;
	return `${(kwp / 1000).toLocaleString('it-IT', { maximumFractionDigits: 2 })} MWp`;
}

/**
 * Energia in MWh. Il mock varia i decimali con la scala: 0 sul totale
 * mensile di portfolio ("2960 MWh"), 1 sulla produzione giornaliera di un
 * singolo impianto ("8.1 MWh") — troppo piccola perché arrotondare a intero
 * non perda informazione.
 */
function fmtMWh(kwh: number, decimals = 0): string {
	// Sotto il megawattora in kWh: «0,0 MWh» per 32 kWh prodotti sembrava zero
	// (INB-348), come «0,06 MWp» per 60 kWp (INB-377).
	if (Math.abs(kwh) < 1000) return `${Math.round(kwh).toLocaleString('it-IT', IT_INT)} kWh`;
	return `${(kwh / 1000).toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} MWh`;
}

/**
 * Riga di confronto col periodo precedente, stile mock ("↑ +12% vs mese
 * prec."). `null` quando il periodo precedente non ha un totale sensato da
 * confrontare (no dati, non "0%" — sarebbe un confronto inventato).
 */
function trendText(deltaPct: number | null): string | null {
	if (deltaPct == null) return null;
	if (deltaPct === 0) return '→ invariato vs mese prec.';
	const arrow = deltaPct > 0 ? '↑ +' : '↓ ';
	return `${arrow}${deltaPct}% vs mese prec.`;
}

const traceFasceMisurate: CalcTraceData = {
	formula: 'fascia(ora) da calendario TIAD\nE_fascia = Σ prelievi delle ore in fascia',
	inputs: [
		{ label: 'Serie oraria riconciliata', value: 'GSE > reale > stimato > tipico', source: 'real' },
		{ label: 'Calendario fasce', value: 'ora civile italiana + festivi nazionali', source: 'norm' },
	],
	norm: 'Delibera ARERA 727/2022/R/eel - TIAD - Fasce F1/F2/F3',
	note: 'Ogni ora misurata è classificata sul calendario civile (feriali/sabato/domenica, festivi nazionali, ora legale). Nessuna quota statistica.',
};

const traceFasceStimate: CalcTraceData = {
	formula: 'E_fascia = E_totale × quota_fascia\nF1 43% · F2 27% · F3 30%',
	inputs: [
		{ label: 'Profili statistici fasce orarie', value: 'TERNA 2024', source: 'norm' },
		{ label: 'Consumo mensile', value: 'stima da profili dichiarati', source: 'config' },
	],
	norm: 'Delibera ARERA 727/2022/R/eel - TIAD - Fasce F1/F2/F3',
	note: 'Nessuna misura oraria disponibile: ripartizione con quote statistiche, non classificata.',
};

// Il mock non mostra più il confronto anno su anno (tolto dalla card): la
// formula riflette solo il CAGR, l'unico numero ancora in vista.
const traceTrend: CalcTraceData = {
	formula: 'CAGR = (Consumo_12M_recenti / Consumo_12M_base)^(1/anni) - 1',
	inputs: [
		{ label: 'Serie oraria riconciliata', value: 'GSE > reale > stimato > tipico', source: 'real' },
	],
	norm: 'TIAD ARERA 727/2022 - misura oraria per POD',
	note: 'Finestre confrontate a distanza di un numero intero di anni: stessi mesi solari, stagionalità annullata. I mesi senza misura restano esclusi dal confronto.',
};

// "% Cond." e "AC" vengono dalla stessa quota energetica per socio della
// Dashboard (`memberSharedEnergy`), non da una media oraria per misuratore che
// non abbiamo a questa granularità.
const tracePerPod: CalcTraceData = {
	formula:
		'% Cond. = kWh_condivisi_socio / kWh_condivisi_configurazione × 100\nAC = kWh_condivisi_socio / kWh_prelevati_socio × 100',
	inputs: [
		{
			label: 'Quota condivisa per socio',
			value: 'stessa formula della Dashboard',
			source: 'real',
		},
	],
	norm: 'TIAD ARERA 727/2022 - quota proporzionale al prelievo del socio',
	note: '"% Cond." è la quota del socio sul totale condiviso della configurazione; "AC" è quanto di quel consumo la quota copre.',
};

// ─── Panoramica configurazioni ───────────────────────────────────────────────

/** Dati di una card della griglia «Panoramica» — le grandezze di portfolio del periodo, per una configurazione sola. */
export interface MonitoringConfigCardData {
	id: string;
	cerNome: string;
	configNome: string;
	/** Comune e area convenzionale/GSE della configurazione (come nella card della Dashboard). */
	comune?: string | null;
	areaCode?: string | null;
	/** Potenza istantanea aggregata, kW. */
	liveKw: number;
	/** Energia prodotta nel periodo, kWh. */
	prodPeriodoKwh: number;
	impiantiOnline: number;
	impiantiTotali: number;
	/** Quota di immessa condivisa nel periodo, 0-100. `null` senza misura. */
	selfConsumptionPct: number | null;
	/** Riepilogo consumi dello stesso periodo selezionato nella panoramica. */
	consumption?: {
		periodKwh: number;
		sharedKwh: number;
		podCount: number;
	};
}

export interface MonitoringConfigCardProps {
	data: MonitoringConfigCardData;
	tab?: MonitoringTab;
	onClick?: () => void;
}

function ConfigMetricCell({
	label,
	value,
	tone,
}: {
	label: string;
	value: string;
	tone: WidgetTone;
}) {
	return (
		<div className="rounded-[7px] border border-border bg-background px-[9px] py-[7px]">
			<div className="mb-0.5 text-[9px] font-extrabold uppercase tracking-wide text-text-muted">
				{label}
			</div>
			<div className="font-mono text-sm font-extrabold" style={{ color: TONE_VAR[tone] }}>
				{value}
			</div>
		</div>
	);
}

/** Card compatta di una configurazione nella «Panoramica» del Monitoraggio: il portfolio del periodo, cliccabile per aprirne il dettaglio. */
export function MonitoringConfigCard({
	data,
	tab = 'produzione',
	onClick,
}: MonitoringConfigCardProps) {
	const impiantiTone: WidgetTone =
		data.impiantiTotali > 0 && data.impiantiOnline < data.impiantiTotali ? 'warn' : 'success';
	const consumption = data.consumption;
	return (
		<button
			type="button"
			onClick={onClick}
			className="w-full cursor-pointer rounded-[11px] border border-border bg-card px-3.5 py-[13px] text-left transition-[border-color,transform] duration-150 hover:-translate-y-0.5 hover:border-accent-purple"
		>
			<div className="mb-3 flex items-start justify-between gap-2">
				<div className="min-w-0">
					<div className="mb-1 truncate text-[9.5px] font-extrabold uppercase tracking-wide text-accent">
						{data.cerNome}
					</div>
					<div className="truncate text-sm font-extrabold leading-tight">{data.configNome}</div>
					<div className="mt-[3px] truncate text-[11px] text-text-muted">
						{data.comune}
						{data.areaCode && (
							<>
								{' · '}
								<span className="font-mono text-accent">{data.areaCode}</span>
							</>
						)}
					</div>
				</div>
				{/* «LIVE» solo se almeno un impianto manda dati: prima era fisso (INB-333). */}
				{tab === 'produzione' &&
					(data.impiantiOnline > 0 ? (
						<span className="inline-flex flex-none items-center gap-1.5">
							<span className="h-2 w-2 animate-pulse-dot rounded-full bg-accent-green" />
							<span className="font-mono text-[9px] font-extrabold text-accent-green">LIVE</span>
						</span>
					) : (
						<span
							className="inline-flex flex-none items-center gap-1.5"
							title="Nessun campione dagli inverter negli ultimi 15 minuti"
						>
							<span className="h-2 w-2 rounded-full bg-text-muted" />
							<span className="font-mono text-[9px] font-extrabold text-text-muted">
								NESSUN DATO
							</span>
						</span>
					))}
			</div>
			<div className="mb-3 grid grid-cols-2 gap-2">
				{tab === 'consumi' ? (
					<>
						<ConfigMetricCell
							label="Consumo periodo"
							value={consumption ? fmtMWh(consumption.periodKwh, 2) : '-'}
							tone="accent"
						/>
						<ConfigMetricCell
							label="Energia condivisa"
							value={consumption ? fmtMWh(consumption.sharedKwh, 2) : '-'}
							tone="success"
						/>
						<ConfigMetricCell
							label="POD di prelievo"
							value={consumption ? String(consumption.podCount) : '-'}
							tone="accent"
						/>
						<ConfigMetricCell
							label="Consumo coperto"
							value={
								consumption && consumption.periodKwh > 0
									? fmtPct((consumption.sharedKwh / consumption.periodKwh) * 100)
									: '-'
							}
							tone="purple"
						/>
					</>
				) : (
					<>
						<ConfigMetricCell
							label="Produzione periodo"
							value={fmtMWh(data.prodPeriodoKwh)}
							tone="success"
						/>
						<ConfigMetricCell label="Potenza live" value={fmtMW(data.liveKw)} tone="accent" />
						<ConfigMetricCell
							label="Impianti online"
							value={`${data.impiantiOnline}/${data.impiantiTotali}`}
							tone={impiantiTone}
						/>
						<ConfigMetricCell
							label="Autoconsumo medio"
							value={data.selfConsumptionPct != null ? fmtPct(data.selfConsumptionPct) : '-'}
							tone="purple"
						/>
					</>
				)}
			</div>
			<Text as="span" variant="caption" weight="bold" tone="accent">
				clic → dettaglio
			</Text>
		</button>
	);
}

export interface MonitoringConfigCardSkeletonProps {
	/** Nome della CER — l'unico dato già noto (arriva dallo scope-tree, non dal fetch di questa card): resta nitido, il resto sfoca. */
	cerNome: string;
}

/** Guscio di {@link MonitoringConfigCard} in caricamento — stessa cornice/padding della card reale. */
export function MonitoringConfigCardSkeleton({ cerNome }: MonitoringConfigCardSkeletonProps) {
	return (
		<div
			className="w-full rounded-[11px] border border-border bg-card px-3.5 py-[13px] text-left"
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio di card generico (`div`), stesso pattern di `PanelCardSkeleton`.
			role="status"
			aria-label={`Caricamento configurazione - ${cerNome}`}
		>
			<div className="mb-3 flex items-start justify-between gap-2">
				<div className="min-w-0 flex-1">
					<div className="mb-1 truncate text-[9.5px] font-extrabold uppercase tracking-wide text-accent">
						{cerNome}
					</div>
					<BodySkeleton className="mb-1.5 h-4 w-32" />
					<BodySkeleton className="h-3 w-40" />
				</div>
				<BodySkeleton className="h-4 w-12 flex-none rounded-full" />
			</div>
			<div className="mb-3 grid grid-cols-2 gap-2">
				{Array.from({ length: 4 }).map((_, i) => (
					<div
						key={i}
						className="rounded-[7px] border border-border bg-background px-[9px] py-[7px]"
					>
						<BodySkeleton className="mb-1 h-2.5 w-20" />
						<BodySkeleton className="h-4 w-16" />
					</div>
				))}
			</div>
			<BodySkeleton className="h-3 w-28" />
		</div>
	);
}

const CONFIG_VIEW_TABS: { value: MonitoringViewMode; label: string }[] = [
	{ value: 'multi', label: 'Panoramica' },
	{ value: 'single', label: 'Configurazione singola' },
];

/**
 * Monitoraggio: produzione degli impianti e consumi della configurazione, sotto
 * due schede.
 *
 * Stanno insieme perché sono la stessa domanda vista dai due lati del contatore
 * — quanta energia è entrata e quanta ne è uscita — e separarle costringeva a
 * confrontare due pagine per capire se un calo di condivisa venisse da meno
 * sole o da meno consumo.
 */
export function MonitoringView({
	anomaliesEnabled = true,
	plants = [],
	portfolio = null,
	plantOptions = [],
	plantDetail = null,
	selectedPlantId = null,
	onSelectPlant,
	members = [],
	sharedEnergy = [],
	energyTotals,
	meterConsumption = [],
	onExplodePod,
	tab: controlledTab,
	onTabChange,
	initialTab = 'produzione',
	period = null,
	onPeriodChange,
	view,
	onViewChange,
	configs = [],
	selectedConfigId = null,
	onSelectConfig,
	overviewGridSlot,
	loading,
}: MonitoringViewProps) {
	const [localTab, setLocalTab] = React.useState<MonitoringTab>(initialTab);
	const tab = controlledTab ?? localTab;
	function selectTab(next: MonitoringTab) {
		if (controlledTab === undefined) setLocalTab(next);
		onTabChange?.(next);
	}
	const [prodTab, setProdTab] = React.useState<ProdSubTab>('panoramica');
	const effectiveProdTab = plants.length === 1 ? 'impianto' : prodTab;
	const periodTabs = React.useMemo(() => buildPeriodTabs(new Date()), []);
	const periodLabel = period
		? (periodTabs.find((t) => t.value === period)?.label ?? period).toLowerCase()
		: 'mese in corso';

	// Il toggle Panoramica/Configurazione-singola esiste solo con più di una
	// configurazione in ambito: con una sola, la pagina è quella di prima.
	const configToggle =
		view != null && onViewChange != null && configs.length > 1 ? { view, onViewChange } : null;
	const hasConfigToggle = configToggle != null;
	const showOverview = configToggle?.view === 'multi';
	const selectedConfig = configs.find((c) => c.id === selectedConfigId) ?? null;
	const pageTitle =
		configToggle?.view === 'single' && selectedConfig ? selectedConfig.configNome : 'Monitoraggio';

	// La barra periodo è comune a Panoramica e configurazione singola: un solo
	// valore, che è quello che il cablaggio interroga.
	const periodSelector = onPeriodChange ? (
		<SegmentedTabs
			items={periodTabs}
			value={period ?? PERIOD_CURRENT}
			onChange={(v) => onPeriodChange(v === PERIOD_CURRENT ? null : v)}
			ariaLabel="Periodo dei dati"
		/>
	) : null;

	const showProdControls = !showOverview && tab === 'produzione' && plants.length > 1;

	return (
		<div>
			<Row gap="sm" className="mb-4 border-b border-border">
				<MasterTab active={tab === 'produzione'} onClick={() => selectTab('produzione')}>
					Produzione
				</MasterTab>
				<MasterTab active={tab === 'consumi'} onClick={() => selectTab('consumi')}>
					Consumi
				</MasterTab>
			</Row>

			{/* Titolo e comandi della produzione sulla stessa riga, come nel mock: le
			    due schede e il selettore d'impianto sono i controlli del titolo, e
			    spariscono insieme al pannello quando si passa ai consumi. */}
			<Row wrap justify="between" gap="md" className="mb-2">
				<PageTitle>{pageTitle}</PageTitle>
				{(hasConfigToggle || showProdControls) && (
					// Il selettore (configurazione o impianto) sta sulla stessa riga
					// delle schede (INB-312): `wrap` lascia che vada a capo sotto di
					// loro solo quando lo spazio non basta, non sempre come prima.
					<Row gap="sm" wrap>
						{configToggle && (
							<SegmentedTabs
								items={CONFIG_VIEW_TABS}
								value={configToggle.view}
								onChange={configToggle.onViewChange}
								ariaLabel="Vista del monitoraggio"
								className="mb-0"
							/>
						)}
						{configToggle?.view === 'single' && onSelectConfig && (
							<select
								value={selectedConfigId ?? ''}
								onChange={(e) => onSelectConfig(e.target.value)}
								aria-label="Configurazione"
								className="min-w-60 rounded-[7px] border border-border bg-card px-2.5 py-1.5 text-[13px] text-text-primary outline-none focus:border-accent"
							>
								{configs.map((c) => (
									<option key={c.id} value={c.id}>
										{c.cerNome} · {c.configNome}
									</option>
								))}
							</select>
						)}
						{showProdControls && (
							<>
								<SegmentedTabs
									items={PROD_TABS}
									value={prodTab}
									onChange={setProdTab}
									ariaLabel="Vista della produzione"
									className="mb-0"
								/>
								{prodTab === 'impianto' && plantOptions.length > 0 && (
									<select
										value={selectedPlantId ?? ''}
										onChange={(e) => onSelectPlant?.(e.target.value || null)}
										aria-label="Impianto da monitorare"
										className="min-w-60 rounded-[7px] border border-border bg-card px-2.5 py-1.5 text-[13px] text-text-primary outline-none focus:border-accent"
									>
										{plantOptions.map((p) => (
											<option key={p.id} value={p.id}>
												{p.nome}
												{p.localita ? ` · ${p.localita}` : ''}
											</option>
										))}
									</select>
								)}
							</>
						)}
					</Row>
				)}
			</Row>

			{periodSelector}

			{showOverview ? (
				// `@container` come nella Dashboard (`DashPanel`): senza, le container
				// query `@2xl:` di `DashGrid layout="configs"` non scattano mai e le
				// card restano incolonnate.
				<div className="@container">
					<Text as="div" variant="label" tone="muted" className="mb-2.5">
						Configurazioni selezionate · clicca una card per il monitoraggio
					</Text>
					<DashGrid layout="configs">{overviewGridSlot}</DashGrid>
				</div>
			) : (
				<>
					{tab === 'produzione' ? (
						<ProductionPanel
							anomaliesEnabled={anomaliesEnabled}
							plants={plants}
							portfolio={portfolio}
							plantDetail={plantDetail}
							subTab={effectiveProdTab}
							periodLabel={periodLabel}
							loading={loading?.production}
							plantDetailLoading={loading?.plantDetail}
							onOpenPlant={(id) => {
								onSelectPlant?.(id);
								setProdTab('impianto');
							}}
						/>
					) : (
						<ConsumptionPanel
							members={members}
							sharedEnergy={sharedEnergy}
							energyTotals={energyTotals}
							meterConsumption={meterConsumption}
							loading={loading?.consumption}
							period={period}
							onExplodePod={onExplodePod}
						/>
					)}
				</>
			)}
		</div>
	);
}
MonitoringView.displayName = 'MonitoringView';

/** Scheda principale: sottolineatura, non pillola — sono due sezioni, non due filtri. */
function MasterTab({
	active,
	onClick,
	children,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={active}
			className={`-mb-px border-b-2 px-3.5 pb-2.5 pt-2 text-[14px] font-bold transition-colors ${
				active
					? 'border-accent-purple text-accent-purple'
					: 'border-transparent text-text-muted hover:text-text-primary'
			}`}
		>
			{children}
		</button>
	);
}

// ─── Pannello Produzione ─────────────────────────────────────────────────────

function ProductionPanel({
	anomaliesEnabled,
	plants,
	portfolio,
	plantDetail,
	subTab,
	periodLabel,
	loading,
	plantDetailLoading,
	onOpenPlant,
}: {
	anomaliesEnabled: boolean;
	plants: MonitoringPlantCard[];
	portfolio: MonitoringPortfolioKpi | null;
	plantDetail: MonitoringPlantDetail | null;
	subTab: ProdSubTab;
	periodLabel: string;
	loading?: boolean;
	plantDetailLoading?: boolean;
	onOpenPlant: (id: string) => void;
}) {
	if (plants.length === 0) {
		// In caricamento non si sa ancora se ci sono impianti: lo skeleton
		// sostituisce il messaggio «nessun impianto», che altrimenti lampeggerebbe
		// per un istante prima di ogni fetch.
		if (loading) return <PlantOverviewSkeleton />;
		return (
			<StatusBlock
				kind="empty"
				message="Nessun impianto di produzione censito. Gli impianti si creano dai misuratori di immissione (M2) nella scheda del socio."
			/>
		);
	}

	return subTab === 'panoramica' ? (
		<PlantOverview
			anomaliesEnabled={anomaliesEnabled}
			plants={plants}
			portfolio={portfolio}
			periodLabel={periodLabel}
			onOpenPlant={onOpenPlant}
		/>
	) : (
		<PlantDetailPanel
			detail={plantDetail}
			periodLabel={periodLabel}
			loading={plantDetailLoading}
			singlePlant={plants.length === 1}
		/>
	);
}

/** Guscio skeleton della `.pcard` di un impianto: titolo, sottotitolo, griglia di celle, riga di stato. */
function PlantCardSkeleton() {
	return (
		<div
			className="pcard"
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio di card generico (`div`), non un `<output>` isolato: il pattern role+aria-label è lo stesso di DistributionBar/PanelCardSkeleton.
			role="status"
			aria-label="Caricamento…"
		>
			<Row className="mb-2.5 items-start justify-between gap-2">
				<span className="block w-2/3 space-y-1.5">
					<BodySkeleton className="h-3.5 w-3/4" />
					<BodySkeleton className="h-2.5 w-1/2" />
				</span>
				<BodySkeleton className="mt-1 h-2 w-2 shrink-0 rounded-full" />
			</Row>
			<div className="mb-2.5 grid grid-cols-2 gap-1.5">
				{Array.from({ length: 4 }).map((_, i) => (
					<span key={i} className="pcell space-y-1">
						<BodySkeleton className="h-2 w-8" />
						<BodySkeleton className="h-3.5 w-12" />
					</span>
				))}
			</div>
			<BodySkeleton className="h-2.5 w-24" />
		</div>
	);
}

/** Skeleton dell'intera Panoramica impianti: 5 KPI, elenco impianti, grafico + KPI portfolio. */
function PlantOverviewSkeleton() {
	return (
		<div>
			<CardGrid columns={4}>
				<PanelCardSkeleton title="Potenza live · inverter">
					<KpiSkeleton />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Produzione mese">
					<KpiSkeleton />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Impianti online">
					<KpiSkeleton />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Autoconsumo medio">
					<KpiSkeleton />
				</PanelCardSkeleton>
			</CardGrid>

			<Text as="div" variant="label" tone="muted" className="mb-2.5">
				Impianti gestiti · clicca una card per il monitoraggio
			</Text>
			<CardGrid columns={4}>
				{Array.from({ length: 4 }).map((_, i) => (
					<PlantCardSkeleton key={i} />
				))}
			</CardGrid>

			<CardGrid columns="2:1">
				<PanelCardSkeleton title="Potenza inverter · portfolio">
					<ChartSkeleton height={320} />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="KPI portfolio">
					<KeyValueListSkeleton rows={8} />
				</PanelCardSkeleton>
			</CardGrid>
		</div>
	);
}

function PlantOverview({
	anomaliesEnabled,
	plants,
	portfolio,
	periodLabel,
	onOpenPlant,
}: {
	anomaliesEnabled: boolean;
	plants: MonitoringPlantCard[];
	portfolio: MonitoringPortfolioKpi | null;
	periodLabel: string;
	onOpenPlant: (id: string) => void;
}) {
	const plantsInFault = plants.filter((p) => p.stato === 'crit');
	const curva = portfolio?.curvaOggi ?? [];
	// La potenza resta su oggi, anche selezionando un mese storico per i KPI.
	// Il giorno restituito permette di riconoscere una risposta ancora in cache.
	const curvaGiorno =
		portfolio && portfolio.giornoCurva !== todayIso() ? dayLabel(portfolio.giornoCurva) : 'oggi';

	return (
		<div>
			{/* Il mock ha una sola riga sotto il numero su queste 5 card (solo
			    `.unit`, nessun `.trend`): niente doppia riga come sulle card
			    Consumi, che invece nel mock ne hanno tre. */}
			<CardGrid columns={4}>
				{/* Telemetria degli inverter, non energia contata: la produzione in kWh
				    viene dal contatore M2 come nelle altre pagine (INB-348). */}
				<PanelCard title="Potenza live · inverter">
					<KpiBig
						// Il mock scrive l'unità dentro il numero grande ("9.77 MW"),
						// non solo nella riga sotto: senza, "0"/"362" restano ambigui.
						value={portfolio ? fmtMW(portfolio.liveKw) : '-'}
						tone="accent"
						unit={
							portfolio
								? `${fmtMWp(portfolio.potenzaTotaleKwp)}${portfolio.prMedio != null ? ` · PR ${fmtPct(Math.round(portfolio.prMedio * 100))}` : ''}`
								: 'nessun campione recente'
						}
					/>
				</PanelCard>
				<PanelCard
					title="Produzione mese"
					aside={
						portfolio && portfolio.energiaPeriodoKwh > 0 ? (
							<StimaInfo
								share={(portfolio.energiaPeriodoStimataKwh ?? 0) / portfolio.energiaPeriodoKwh}
							/>
						) : undefined
					}
				>
					<KpiBig
						value={portfolio ? fmtMWh(portfolio.energiaPeriodoKwh) : '-'}
						tone="success"
						unit={(portfolio && trendText(portfolio.energiaPeriodoDeltaPct)) || periodLabel}
					/>
				</PanelCard>
				<PanelCard title="Impianti online">
					<KpiBig
						value={portfolio ? `${portfolio.impiantiOnline}/${portfolio.impiantiTotali}` : '-'}
						tone={
							portfolio && portfolio.impiantiOnline < portfolio.impiantiTotali ? 'warn' : 'success'
						}
						// Il mock nomina il campo in fault ("1 campo in fault"), non solo
						// il conteggio: qui il nome reale del primo impianto critico se
						// ce n'è uno solo, il conteggio se sono di più.
						unit={
							!anomaliesEnabled
								? 'Stato connessioni inverter'
								: plantsInFault.length === 0
									? 'nessuna anomalia aperta'
									: plantsInFault.length === 1
										? `${plantsInFault[0]?.nome} in fault`
										: `${plantsInFault.length} impianti in fault`
						}
					/>
				</PanelCard>
				<PanelCard title="Autoconsumo medio">
					<KpiBig
						value={
							portfolio?.selfConsumptionPct != null ? fmtPct(portfolio.selfConsumptionPct) : '-'
						}
						tone="accent"
						unit={
							portfolio?.selfConsumptionPct != null
								? 'media portfolio'
								: 'nessuna misura nel periodo'
						}
					/>
				</PanelCard>
			</CardGrid>

			<Text as="div" variant="label" tone="muted" className="mb-2.5">
				Impianti gestiti · clicca una card per il monitoraggio
			</Text>
			<CardGrid columns={4}>
				{plants.map((p) => (
					<button key={p.id} type="button" onClick={() => onOpenPlant(p.id)} className="pcard">
						<Row className="mb-2.5 items-start justify-between gap-2">
							<span>
								<Text as="span" variant="subtitle" weight="bold" className="block">
									{p.nome}
								</Text>
								<Text as="span" variant="caption" tone="muted" className="block">
									{[p.localita, p.tipo ? TIPO_LABEL[p.tipo] : null].filter(Boolean).join(' · ')}
								</Text>
							</span>
							{/* Il pallino non ha etichetta perché lo stato è scritto per esteso
							    in fondo alla card: darne due letture confonderebbe. */}
							<span
								className="stdot mt-1"
								style={{ background: statoVisto(p).color }}
								aria-hidden
							/>
						</Row>
						<div className="mb-2.5 grid grid-cols-2 gap-1.5">
							<PlantCell label="Live" value={fmtMW(p.liveKw)} />
							<PlantCell
								label="PR"
								value={p.pr != null ? `${Math.round(p.pr * 100)}%` : '-'}
								alert={p.pr != null && p.prTarget != null && p.pr < p.prTarget * 0.85}
							/>
							<PlantCell label="Potenza" value={fmtMWp(p.potenzaKwp)} />
							<PlantCell label="Inverter" value={`${p.inverterOk}/${p.inverterTotali}`} />
						</div>
						<Row className="items-center justify-between gap-2">
							<Text as="span" variant="micro" tone="accent">
								clic → {anomaliesEnabled && p.stato === 'crit' ? 'anomalie' : 'monitoraggio'}
							</Text>
							<Row gap="xs">
								{anomaliesEnabled && p.anomalieAperte > 0 && (
									<Text as="span" variant="micro" tone="muted">
										{p.anomalieAperte} {p.anomalieAperte === 1 ? 'anomalia' : 'anomalie'}
									</Text>
								)}
								<Text
									as="span"
									variant="inherit"
									className="font-mono text-[9px] font-extrabold"
									style={{ color: statoVisto(p).color }}
								>
									{statoVisto(p).label}
								</Text>
							</Row>
						</Row>
					</button>
				))}
			</CardGrid>

			<CardGrid columns="2:1">
				<PanelCard title={`Potenza inverter · portfolio · ${curvaGiorno}`}>
					<SlChart
						series={[
							{
								name: 'Effettiva',
								// Colore di serie fisso (non `--color-warn`): il mock scrive
								// '#f59e0b' come letterale, non dal tema, quindi in chiaro non
								// deve scurirsi a '#b45309' come farebbe il token semantico.
								color: 'var(--color-series-2)',
								kind: 'area',
								// /1000: il mock disegna questo grafico in MW, non kW — non
								// solo l'etichetta dell'unità, anche i valori, altrimenti
								// l'asse mostrerebbe numeri in scala kW sotto "MW".
								vals: curva.map((c) => c.ac / 1000),
							},
							{
								// Attesa dall'irraggiamento reale della località, non dalla
								// telemetria: un impianto sano segue questa curva, non la supera.
								name: 'Nominale',
								color: 'var(--color-series-4)',
								vals: curva.map((c) => (c.nominale ?? c.dc) / 1000),
							},
						]}
						// Il mock etichetta l'asse solo con l'ora ("06", non "06:00" —
						// `PRODHL=['06','','08',…]`): il minuto resta nel tooltip
						// (`hoverX`, dove il mock stesso lo scrive per esteso).
						x={curva.map((c, i) => (i % 8 === 0 ? c.ora.slice(0, 2) : ''))}
						hoverX={(i) => curva[i]?.ora ?? ''}
						unit="MW"
						height={320}
						ariaLabel="Potenza aggregata del portfolio nella giornata"
						emptyMessage="Nessun campione live dagli inverter"
					/>
				</PanelCard>
				<PanelCard title="KPI portfolio">
					{portfolio ? (
						<Stack gap="none">
							<PortfolioRow label="Impianti totali" value={String(portfolio.impiantiTotali)} />
							<PortfolioRow
								label="Composizione"
								value={
									portfolio.composizione
										.map((c) => `${c.count} ${c.tipo ? TIPO_LABEL[c.tipo] : 'non classificati'}`)
										.join(' · ') || '-'
								}
							/>
							<PortfolioRow label="Potenza totale" value={fmtMWp(portfolio.potenzaTotaleKwp)} />
							<PortfolioRow
								label="Inverter monitorati"
								value={String(portfolio.inverterMonitorati)}
							/>
							<PortfolioRow
								label="Stringhe monitorate"
								value={fmtNum(portfolio.stringheMonitorate)}
							/>
							<PortfolioRow
								label="PR medio portfolio"
								value={
									portfolio.prMedio != null ? fmtPct(Math.round(portfolio.prMedio * 100)) : '-'
								}
							/>
							<PortfolioRow
								label="Disponibilità"
								value={
									portfolio.disponibilita != null
										? fmtPct(Math.round(portfolio.disponibilita * 100))
										: '-'
								}
							/>
							{anomaliesEnabled && (
								<PortfolioRow
									label="MTTR medio"
									value={
										portfolio.mttrMedioOre != null ? `${portfolio.mttrMedioOre.toFixed(1)} h` : '-'
									}
								/>
							)}
						</Stack>
					) : (
						<StatusBlock
							kind="empty"
							size="sm"
							message="KPI disponibili col consolidato giornaliero."
						/>
					)}
				</PanelCard>
			</CardGrid>
		</div>
	);
}

function PlantCell({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
	return (
		<span className="pcell">
			<span className="l">{label}</span>
			<span className="v" style={alert ? { color: 'var(--color-danger)' } : undefined}>
				{value}
			</span>
		</span>
	);
}

function PortfolioRow({ label, value }: { label: string; value: string }) {
	return (
		<Row className="items-center justify-between border-b border-border py-2 last:border-b-0">
			<Text as="span" variant="body" tone="muted">
				{label}
			</Text>
			<Text as="span" variant="dataSm" tone="purple">
				{value}
			</Text>
		</Row>
	);
}

const INVERTER_TONE: Record<
	MonitoringPlantInverter['stato'],
	'green' | 'orange' | 'warn' | 'blue'
> = {
	ok: 'green',
	warn: 'orange',
	// `Tag` non ha un tono rosso proprio: `warn` è il suo estremo alto.
	fault: 'warn',
	offline: 'blue',
};

const INVERTER_LABEL: Record<MonitoringPlantInverter['stato'], string> = {
	ok: 'Regolare',
	warn: 'Attenzione',
	fault: 'Guasto',
	offline: 'Muto',
};

/** Skeleton del dettaglio impianto: 4 KPI, 2 grafici, tabella inverter. */
function PlantDetailPanelSkeleton() {
	return (
		<div>
			<CardGrid columns={4}>
				<PanelCardSkeleton title="Produzione">
					<KpiSkeleton />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Performance Ratio">
					<KpiSkeleton />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Irraggiamento POA">
					<KpiSkeleton />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Temperatura moduli">
					<KpiSkeleton />
				</PanelCardSkeleton>
			</CardGrid>
			<CardGrid columns={2}>
				<PanelCardSkeleton title="Potenza DC/AC inverter">
					<ChartSkeleton height={300} />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Produzione giornaliera">
					<ChartSkeleton height={300} />
				</PanelCardSkeleton>
			</CardGrid>
			<Stack mb="lg">
				<PanelCardSkeleton title="Dettaglio inverter & stringhe">
					<ListSkeleton rows={4} avatar={false} />
				</PanelCardSkeleton>
			</Stack>
		</div>
	);
}

function PlantDetailPanel({
	detail,
	periodLabel,
	loading,
	singlePlant,
}: {
	detail: MonitoringPlantDetail | null;
	periodLabel: string;
	loading?: boolean;
	singlePlant: boolean;
}) {
	if (!detail) {
		// In caricamento non si sa ancora se l'impianto scelto ha un dettaglio:
		// lo skeleton sostituisce l'invito a scegliere un impianto, che qui non
		// avrebbe senso — l'impianto è già scelto, si aspetta solo la risposta.
		if (loading) return <PlantDetailPanelSkeleton />;
		return (
			<StatusBlock
				kind="empty"
				message={
					singlePlant
						? "Dati dell'impianto non disponibili."
						: 'Scegli un impianto dal selettore per vederne il dettaglio.'
				}
			/>
		);
	}
	const { plant, inverters, curva, mensile, curvaTipo } = detail;
	// Un impianto piccolo produce qualche centinaio di kWh al giorno: in MWh il
	// grafico diventava una fila di 0,2-0,3.
	const mensileScala = mensile.some((m) => (m.energiaKwh ?? 0) >= 1000) ? 1000 : 1;
	// I KPI seguono il periodo; la curva "Potenza DC/AC" segue la giornata
	// corrente sul mese in corso, tre giornate tipo stimate su un mese passato
	// — la telemetria minuto per minuto dell'inverter non copre un mese già
	// chiuso (vedi `MonitoringPlantDetail.curvaTipo`).
	const isToday = detail.giorno === todayIso();
	const giorno = isToday ? 'oggi' : dayLabel(detail.giorno);
	const curvaGiorno = detail.giornoCurva === todayIso() ? 'oggi' : dayLabel(detail.giornoCurva);
	const TYPICAL_COLORS: Record<MonitoringPlantTypicalDaySeries['label'], string> = {
		Picco: 'var(--color-series-2)',
		Media: 'var(--color-series-1)',
		Peggiore: 'var(--color-series-5)',
	};

	const inverterColumns: DataTableColumn<MonitoringPlantInverter>[] = [
		{ key: 'codice', header: 'Inverter', render: (i) => i.codice },
		{
			key: 'live',
			header: 'Live',
			variant: 'monoAccent',
			render: (i) => fmtMW(i.liveKw),
		},
		{
			key: 'pr',
			header: 'PR',
			variant: 'monoAccent',
			render: (i) => (i.pr != null ? fmtPct(Math.round(i.pr * 100)) : '-'),
		},
		{
			key: 'stato',
			header: 'Stato',
			render: (i) => <Tag tone={INVERTER_TONE[i.stato]}>{INVERTER_LABEL[i.stato]}</Tag>,
		},
	];

	return (
		<div>
			{/* Riga di stato sopra i KPI: quale impianto si sta guardando e in che
			    condizioni. Nel mock precede la griglia perché i quattro numeri sotto
			    vanno letti sapendo se l'impianto è in allarme. */}
			<Row wrap gap="sm" className="mb-4">
				<span className="stdot" style={{ background: statoVisto(plant).color }} aria-hidden />
				<Text as="span" variant="caption" weight="bold" style={{ color: statoVisto(plant).color }}>
					{statoVisto(plant).label}
				</Text>
				<Text as="span" variant="caption" tone="muted">
					{[
						plant.nome,
						plant.localita,
						plant.tipo ? TIPO_LABEL[plant.tipo] : null,
						fmtMWp(plant.potenzaKwp),
					]
						.filter(Boolean)
						.join(' · ')}
				</Text>
			</Row>

			<CardGrid columns={4}>
				<PanelCard
					title={`Produzione · ${giorno}`}
					aside={
						plant.energiaGiornoKwh ? (
							<StimaInfo share={(plant.energiaGiornoStimataKwh ?? 0) / plant.energiaGiornoKwh} />
						) : undefined
					}
				>
					<KpiBig
						value={plant.energiaGiornoKwh != null ? fmtMWh(plant.energiaGiornoKwh, 1) : '-'}
						tone="success"
						unit={`${fmtMWp(plant.potenzaKwp)} installati`}
						trendTone="muted"
					/>
				</PanelCard>
				<PanelCard title="Performance Ratio">
					<KpiBig
						value={plant.pr != null ? fmtPct(Math.round(plant.pr * 100)) : '-'}
						tone={
							plant.pr != null && plant.prTarget != null && plant.pr < plant.prTarget * 0.85
								? 'warn'
								: 'accent'
						}
						unit={
							plant.prTarget != null
								? `target ≥ ${Math.round(plant.prTarget * 100)}%`
								: 'del giorno'
						}
						trend={
							plant.pr != null
								? `misurato sul consolidato di ${giorno}`
								: 'in attesa del consolidato'
						}
						trendTone="muted"
					/>
				</PanelCard>
				<PanelCard title="Irraggiamento POA">
					<KpiBig
						value={detail.poaWm2 != null ? fmtNum(Math.round(detail.poaWm2)) : '-'}
						tone="warn"
						unit="W/m² medi sul piano moduli"
						trend="da cache meteo della località"
						trendTone="muted"
					/>
				</PanelCard>
				<PanelCard title="Temperatura moduli">
					<KpiBig
						value={detail.tModuliC != null ? `${Math.round(detail.tModuliC)}` : '-'}
						tone="accent"
						unit="°C medi"
						trend={`${plant.inverterOk}/${plant.inverterTotali} inverter regolari`}
						trendTone="muted"
					/>
				</PanelCard>
			</CardGrid>

			<CardGrid columns={2}>
				{curvaTipo ? (
					<PanelCard title="Potenza AC · giornata tipo">
						<SlChart
							series={curvaTipo.map((s) => ({
								name: `${s.label} · ${dayLabel(s.giorno)}`,
								color: TYPICAL_COLORS[s.label],
								vals: s.punti.map((p) => p.ac / 1000),
							}))}
							x={(curvaTipo[0]?.punti ?? []).map((p, i) => (i % 8 === 0 ? p.ora.slice(0, 2) : ''))}
							hoverX={(i) => curvaTipo[0]?.punti[i]?.ora ?? ''}
							unit="MW"
							height={300}
							ariaLabel="Potenza AC stimata nelle tre giornate tipo del mese"
							emptyMessage="Nessun giorno con produzione nel mese"
						/>
						<Footnote>
							* Stima: forma solare tipica scalata sull'energia realmente prodotta in ciascun giorno
							- la telemetria minuto per minuto non copre un mese già chiuso.
						</Footnote>
					</PanelCard>
				) : (
					<PanelCard title={`Potenza DC/AC inverter · ${curvaGiorno}`}>
						<SlChart
							series={[
								{
									name: 'Potenza AC (inverter)',
									// Vedi nota su "Effettiva/Nominale" sopra: colore di serie
									// fisso, non dal tema.
									color: 'var(--color-series-2)',
									kind: 'area',
									vals: curva.map((c) => c.ac / 1000),
								},
								{
									name: 'Potenza DC (pannelli)',
									color: 'var(--color-series-4)',
									vals: curva.map((c) => c.dc / 1000),
								},
							]}
							// Il mock etichetta l'asse solo con l'ora ("06", non "06:00" —
							// `PRODHL=['06','','08',…]`): il minuto resta nel tooltip
							// (`hoverX`, dove il mock stesso lo scrive per esteso).
							x={curva.map((c, i) => (i % 8 === 0 ? c.ora.slice(0, 2) : ''))}
							hoverX={(i) => curva[i]?.ora ?? ''}
							unit="MW"
							height={300}
							ariaLabel="Potenza in continua e alternata nella giornata"
							emptyMessage="Nessun campione dagli inverter per oggi"
						/>
					</PanelCard>
				)}
				<PanelCard title={`Produzione giornaliera · ${periodLabel}`}>
					<SlChart
						series={[
							{
								name: 'Produzione',
								// Serie sola: nel mock è ciano, non ambra. Con --color-accent
								// virava a #0e7490 in tema chiaro.
								color: 'var(--color-series-1)',
								kind: 'area',
								vals: mensile.map((m) => (m.energiaKwh ?? 0) / mensileScala),
							},
						]}
						x={mensile.map((m, i) =>
							i % 5 === 0 || i === mensile.length - 1 ? m.giorno.slice(8) : '',
						)}
						hoverX={(i) => mensile[i]?.giorno ?? ''}
						unit={mensileScala === 1 ? 'kWh' : 'MWh'}
						height={300}
						ariaLabel="Produzione giorno per giorno nel mese"
						emptyMessage="Nessuna curva del contatore di produzione nel periodo"
					/>
				</PanelCard>
			</CardGrid>

			<Stack mb="lg">
				<PanelCard title="Dettaglio inverter & stringhe">
					{inverters.length === 0 ? (
						<StatusBlock
							kind="empty"
							size="sm"
							message="Nessun inverter censito per questo impianto."
						/>
					) : (
						<DataTable columns={inverterColumns} rows={inverters} rowKey={(i) => i.id} />
					)}
				</PanelCard>
			</Stack>
		</div>
	);
}

// ─── Pannello Consumi ────────────────────────────────────────────────────────

/** Skeleton della scheda Consumi: 6 KPI, trend + produzione/condivisa, fasce + top POD. */
function ConsumptionPanelSkeleton() {
	return (
		<div>
			<CardGrid columns="lead">
				<PanelCardSkeleton title="Consumo Totale">
					<KpiSkeleton />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Residenziale">
					<KpiSkeleton />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Uffici">
					<KpiSkeleton />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Commerciale">
					<KpiSkeleton />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Industriale">
					<KpiSkeleton />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Ente Pubblico">
					<KpiSkeleton />
				</PanelCardSkeleton>
			</CardGrid>

			<CardGrid columns={2}>
				<PanelCardSkeleton title="Trend Consumi 12 Mesi" badge>
					<ChartSkeleton height={300} />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Produzione FV vs Energia Condivisa" badge>
					<ChartSkeleton height={300} />
				</PanelCardSkeleton>
			</CardGrid>

			<CardGrid columns={2}>
				<PanelCardSkeleton title="Consumo per Fascia Oraria" badge>
					<BarsSkeleton rows={3} />
				</PanelCardSkeleton>
				<PanelCardSkeleton title="Top 5 POD per Consumo" badge>
					<ListSkeleton rows={5} avatar={false} />
				</PanelCardSkeleton>
			</CardGrid>
		</div>
	);
}

function ConsumptionPanel({
	members,
	sharedEnergy,
	energyTotals: totals,
	meterConsumption,
	period,
	loading,
	onExplodePod,
}: {
	members: EnergyMember[];
	sharedEnergy: ConsumptionTrendInput[];
	energyTotals: MonitoringEnergyTotals;
	meterConsumption: MonitoringMeterConsumption[];
	period: string | null;
	loading?: boolean;
	onExplodePod?: (t: MonitoringPodTarget) => void;
}) {
	// Stato di sola presentazione (quale vista del trend è aperta) — il mock
	// tiene due sotto-schede sul grafico "Trend Consumi 12 Mesi".
	const [trendView, setTrendView] = React.useState<'cumulato' | 'tipologia'>('cumulato');

	const trend = React.useMemo(() => buildConsumptionTrend(sharedEnergy), [sharedEnergy]);

	const activeMembers = members
		.filter((m) => m.stato !== 'withdrawn')
		.sort((a, b) => {
			const ord: Record<string, number> = { producer: 0, prosumer: 1, consumer: 2 };
			return (ord[a.ruolo] ?? 2) - (ord[b.ruolo] ?? 2);
		});

	const hasRealData = totals.totalPrelevata > 0;
	const estimated = calcEstimatedTotals(activeMembers);
	const totalConsAnnuo = hasRealData ? totals.totalPrelevata : estimated.totalAnnualKwh;

	// Mese di riferimento: quello scelto in cima. Tutta la scheda (KPI, fasce, per
	// tipo, classifica POD) si riferisce allo stesso mese, altrimenti le quote
	// non si sommerebbero al totale mostrato in testa.
	const { year: refYear, month: refMonthNum } = periodMonth(period);
	// Il periodo è una scelta di calendario, non un inventario: un mese senza
	// misura resta selezionabile e i pannelli mostrano i loro vuoti.
	const refMonth = trend.months.find((m) => m.year === refYear && m.month === refMonthNum) ?? null;
	const isCurrentPeriod = period == null;
	const totalConsMensile = refMonth
		? refMonth.prelevata
		: // La stima da profili vale solo per il mese in corso: applicarla a un mese
			// passato che l'utente ha scelto apposta darebbe un numero che non è né
			// misurato né di quel mese.
			isCurrentPeriod
			? hasRealData
				? Math.round(totalConsAnnuo / 12)
				: estimated.currentMonthKwh
			: 0;

	// Il mese scelto si nomina anche quando non ha misura: è l'intestazione della
	// scheda, non un'etichetta del dato.
	const meseNome = monthName(refMonthNum - 1);
	const meseLabel = `${meseNome} ${refYear}`;

	const hasMeterData = meterConsumption.length > 0;

	const monthPoints = React.useMemo(
		() =>
			sharedEnergy.filter((point) => {
				const date = point.hour instanceof Date ? point.hour : new Date(point.hour);
				return date.getFullYear() === refYear && date.getMonth() + 1 === refMonthNum;
			}),
		[sharedEnergy, refYear, refMonthNum],
	);

	// Il mock v11 confronta produzione FV ed energia condivisa giorno per
	// giorno nel mese di riferimento (curva, non barre mensili su 12 mesi) —
	// aggregazione giornaliera della stessa serie oraria di `monthPoints`.
	const dailyProdCondivisa = React.useMemo(() => {
		if (monthPoints.length === 0) return undefined;
		const byDay = new Map<number, { prod: number; cond: number }>();
		for (const point of monthPoints) {
			const date = point.hour instanceof Date ? point.hour : new Date(point.hour);
			const day = date.getDate();
			const bucket = byDay.get(day) ?? { prod: 0, cond: 0 };
			bucket.prod += point.totalImmessa;
			bucket.cond += point.energiaCondivisa;
			byDay.set(day, bucket);
		}
		const entries = [...byDay.entries()].sort(([a], [b]) => a - b);
		return {
			days: entries.map(([day]) => day),
			prod: entries.map(([, bucket]) => bucket.prod),
			cond: entries.map(([, bucket]) => bucket.cond),
		};
	}, [monthPoints]);

	// Il mock mostra il consumo cumulato sui 12 mesi (curva sempre crescente),
	// non il valore mensile punto per punto: somma progressiva di trend.months.
	const cumulativeConsumo = React.useMemo(() => {
		let running = 0;
		return trend.months.map((m) => {
			running += m.prelevata;
			return running;
		});
	}, [trend.months]);

	const bands = React.useMemo(
		() => buildTimeBandBreakdown(monthPoints, { fallbackTotalKwh: totalConsMensile }),
		[monthPoints, totalConsMensile],
	);

	const byGroup = React.useMemo(() => {
		const acc: Record<UserGroup, number> = { res: 0, com: 0, pa: 0 };
		if (hasMeterData) {
			for (const meter of meterConsumption) acc[groupOf(meter.memberType)] += meter.prelevata;
			return acc;
		}
		acc.res = estimated.byType.residential;
		acc.com = estimated.byType.commercial;
		acc.pa = estimated.byType.pa;
		return acc;
	}, [hasMeterData, meterConsumption, estimated]);

	const groupTotal = byGroup.res + byGroup.com + byGroup.pa;
	const pctOf = (kwh: number) => (groupTotal > 0 ? Math.round((kwh / groupTotal) * 100) : 0);

	const paMembers = activeMembers.filter((m) => PA_TYPES.has(m.tipo));

	const podColumns: DataTableColumn<MonitoringMeterConsumption>[] = [
		{
			key: 'pod',
			header: 'POD',
			render: (m) => m.codice || '-',
		},
		{
			key: 'socio',
			header: 'Socio',
			render: (m) => m.memberName || 'socio non attivo',
		},
		{
			key: 'tipo',
			header: 'Tipo',
			render: (m) => {
				const group = groupOf(m.memberType);
				return (
					<Tag tone={group === 'com' ? 'purple' : group === 'pa' ? 'blue' : 'green'}>
						{group === 'com' ? 'PMI' : group === 'pa' ? 'PA' : 'Res.'}
					</Tag>
				);
			},
		},
		{
			key: 'consumo',
			header: 'Consumo',
			variant: 'monoAccent',
			render: (m) => `${fmtNum(m.prelevata)} kWh`,
		},
		{
			key: 'condivisa',
			header: '% Cond.',
			variant: 'muted',
			render: (m) =>
				m.pctCondivisaConfig != null ? (
					<>
						{m.pctCondivisaConfig}%
						<MiniBar pct={m.pctCondivisaConfig} />
					</>
				) : (
					'-'
				),
		},
		{
			key: 'ac',
			header: 'AC',
			// Soglia 70%, non 65%: l'ai-box del mock parla di "sotto il 65%" ma si
			// riferisce a tutti i POD, non solo ai 5 di questa card. I tag reali
			// del mock danno 68%→warn e 74%→done, quindi la soglia che separa le
			// due righe è 70.
			render: (m) =>
				m.pctAutoconsumo != null ? (
					<Tag tone={m.pctAutoconsumo >= 70 ? 'done' : 'warn'}>{m.pctAutoconsumo}%</Tag>
				) : (
					'-'
				),
		},
	];

	if (loading) return <ConsumptionPanelSkeleton />;

	return (
		<div>
			{/* Il mock apre la scheda Consumi direttamente sulle card, senza riga di
			    ambito (configurazione/mese/badge dati): il titolo di pagina è già
			    «Monitoraggio» e la barra periodo comune sopra dice già il mese. */}

			{/* Le 6 card KPI restano pulite come nel mock — solo titolo, senza badge né
			    pulsante "esplodi": con quel contenuto extra la riga non ci stava senza
			    scroll orizzontale, oltre a discostarsi dallo stile del mock. Il
			    drill-down per POD resta sulle card sotto (Trend, Fascia Oraria, Top 5
			    POD, Produzione FV vs Energia Condivisa). */}
			<CardGrid columns="lead">
				<PanelCard title="Consumo Totale">
					<KpiBig
						value={totalConsMensile > 0 ? fmtNum(totalConsMensile) : '-'}
						tone="accent"
						unit="kWh consumati nel mese"
						trend="ripartito per tipologia utenza"
						trendTone="muted"
					/>
				</PanelCard>

				<PanelCard title="Residenziale">
					<KpiBig
						value={byGroup.res > 0 ? fmtNum(byGroup.res) : '-'}
						tone="success"
						unit={byGroup.res > 0 ? `kWh · ${pctOf(byGroup.res)}%` : 'kWh consumo mensile'}
						trend="famiglie e condomini"
						trendTone="muted"
					/>
				</PanelCard>

				{/* Il mock v11 mostra Uffici e Industriale come categorie separate da
				    Commerciale, ma member_type (schema) non le distingue: sono tutte
				    'sme'. Card segnaposto per lo stesso layout a 6 colonne — nessun
				    numero inventato, in attesa di un campo dati che le distingua
				    davvero. */}
				<PanelCard title="Uffici">
					<KpiBig
						value="-"
						tone="muted"
						unit="dato non disponibile"
						trend="non distinta nei dati soci"
						trendTone="muted"
					/>
				</PanelCard>

				<PanelCard title="Commerciale">
					<KpiBig
						value={byGroup.com > 0 ? fmtNum(byGroup.com) : '-'}
						tone="purple"
						unit={byGroup.com > 0 ? `kWh · ${pctOf(byGroup.com)}%` : 'kWh consumo mensile'}
						trend="negozi e retail"
						trendTone="muted"
					/>
				</PanelCard>

				<PanelCard title="Industriale">
					<KpiBig
						value="-"
						tone="muted"
						unit="dato non disponibile"
						trend="non distinta nei dati soci"
						trendTone="muted"
					/>
				</PanelCard>

				<PanelCard title="Ente Pubblico">
					<KpiBig
						value={byGroup.pa > 0 ? fmtNum(byGroup.pa) : '-'}
						tone="warn"
						unit={byGroup.pa > 0 ? `kWh · ${pctOf(byGroup.pa)}%` : 'kWh consumo mensile'}
						trend={
							paMembers.length > 0
								? paMembers
										.map((m) => memberLabel(m))
										.join(', ')
										.slice(0, 28)
								: 'nessun ente pubblico'
						}
						trendTone="muted"
					/>
				</PanelCard>
			</CardGrid>

			<CardGrid columns={2}>
				<PanelCard
					title="Trend Consumi 12 Mesi"
					aside={
						<Row gap="sm">
							<CalcTrace data={traceTrend} />
						</Row>
					}
				>
					<SegmentedTabs
						items={[
							{ value: 'cumulato', label: 'Cumulato' },
							{ value: 'tipologia', label: 'Per tipologia' },
						]}
						value={trendView}
						onChange={setTrendView}
						ariaLabel="Vista del trend consumi"
					/>
					{trendView === 'cumulato' ? (
						<SlChart
							series={[
								{
									name: 'Consumo cumulato',
									// Vedi nota su "Effettiva/Nominale" più sopra: il mock scrive
									// '#00AEEF' come letterale, colore fisso non dal tema.
									color: 'var(--color-series-1)',
									kind: 'area',
									vals: cumulativeConsumo,
								},
							]}
							x={trend.months.map((m) => m.label)}
							unit="kWh"
							height={300}
							emptyMessage="Trend disponibile dopo l'integrazione con i misuratori SII/GSE"
						/>
					) : (
						<SlChart
							series={[
								{ name: 'Residenziale', color: 'var(--color-series-4)', vals: [] },
								{ name: 'Commerciale', color: 'var(--color-series-5)', vals: [] },
								{ name: 'Ente Pubblico', color: 'var(--color-series-2)', vals: [] },
							]}
							x={trend.months.map((m) => m.label)}
							unit="kWh"
							height={300}
							emptyMessage="Ripartizione per tipologia nel tempo non disponibile: richiede lo storico dei consumi per categoria socio, non solo il totale mensile."
						/>
					)}
					{trend.cagrPct !== null && (
						<InfoStrip>
							📈 CAGR consumi:{' '}
							<Text as="strong" variant="inherit" tone="success">
								{trend.cagrPct > 0 ? '+' : ''}
								{trend.cagrPct}%
							</Text>{' '}
							· tasso annuo composto su {trend.cagrYears} {trend.cagrYears === 1 ? 'anno' : 'anni'}{' '}
							di storico
						</InfoStrip>
					)}
					<Footnote>
						* Aggregazione mensile della serie oraria riconciliata (GSE &gt; bolletta &gt; letture
						&gt; stima). I mesi senza misura non compaiono e restano fuori dai confronti.
					</Footnote>
				</PanelCard>

				{/* `fill` + `fillHeight`, non un `height` in px tarato a occhio: un
				    numero fisso si disallineava ogni volta che il footnote della
				    card gemella andava a capo diversamente (dipende dalla larghezza
				    reale del viewport, non riproducibile in Storybook). Così il
				    grafico cresce con lo spazio che la griglia gli dà, sempre alla
				    stessa quota della gemella, qualunque sia la sua altezza — MA
				    solo fino al banner CAGR/footnote inclusi: quella card ha un
				    banner opzionale + una nota a piè di grafico che questa non ha,
				    quindi senza compensarli `fillHeight` scendeva oltre la base
				    reale del grafico gemello, fino in fondo alla card. Spaziatori
				    invisibili (`invisible`, altezza riservata ma senza testo/senza
				    lettura per lo screen reader) con lo stesso identico testo —
				    non un'altezza fissa — così l'a-capo resta identico a qualunque
				    larghezza di viewport, stessa logica di `fillHeight` stesso. */}
				<PanelCard title="Produzione FV vs Energia Condivisa" fill>
					<SlChart
						series={[
							{
								name: 'Produzione FV',
								color: 'var(--color-series-2)',
								kind: 'area',
								vals: dailyProdCondivisa?.prod ?? [],
							},
							{
								name: 'Energia condivisa',
								color: 'var(--color-series-1)',
								vals: dailyProdCondivisa?.cond ?? [],
							},
						]}
						x={(dailyProdCondivisa?.days ?? []).map((d, i) => (i % 5 === 0 ? String(d) : ''))}
						hoverX={(i) => String(dailyProdCondivisa?.days[i] ?? '')}
						unit="kWh"
						fillHeight
						ariaLabel="Produzione fotovoltaica ed energia condivisa nel mese di riferimento"
						emptyMessage="Nessuna misura oraria nel mese di riferimento"
					/>
					{trend.cagrPct !== null && (
						<div className="invisible" aria-hidden="true">
							<InfoStrip>
								📈 CAGR consumi:{' '}
								<Text as="strong" variant="inherit" tone="success">
									{trend.cagrPct > 0 ? '+' : ''}
									{trend.cagrPct}%
								</Text>{' '}
								· tasso annuo composto su {trend.cagrYears}{' '}
								{trend.cagrYears === 1 ? 'anno' : 'anni'} di storico
							</InfoStrip>
						</div>
					)}
					<div className="invisible" aria-hidden="true">
						<Footnote>
							* Aggregazione mensile della serie oraria riconciliata (GSE &gt; bolletta &gt; letture
							&gt; stima). I mesi senza misura non compaiono e restano fuori dai confronti.
						</Footnote>
					</div>
				</PanelCard>
			</CardGrid>

			<CardGrid columns={2}>
				<PanelCard
					title="Consumo per Fascia Oraria"
					aside={
						<Row gap="sm">
							<CalcTrace data={bands.measured ? traceFasceMisurate : traceFasceStimate} />
						</Row>
					}
				>
					<TimeBandBar
						totalKwh={bands.totalPrelevata}
						f1Pct={bands.byFascia.F1.pct / 100}
						f2Pct={bands.byFascia.F2.pct / 100}
						formatNumber={fmtNum}
					/>

					{bands.bands.map((band) => (
						<ProgressItem
							key={band.fascia}
							label={FASCIA_LABEL_MOCK[band.fascia]}
							valueLabel={`${band.pct}%${band.prelevata > 0 ? ` · ${fmtNum(band.prelevata)} kWh` : ''}`}
							tone={BAND_TONE[band.fascia]}
							pct={band.pct}
							hint={
								bands.measured
									? `${band.hours} ore${band.condivisa > 0 ? ` · condivisa ${fmtNum(band.condivisa)} kWh` : ''}`
									: undefined
							}
						/>
					))}

					{bands.measured ? (
						<AiBox>
							{`💡 Il ${bands.byFascia.F1.pct}% del consumo di ${meseLabel} cade in F1, contro il ${Math.round(TIME_BANDS.F1.share * 100)}% del profilo statistico nazionale. `}
							{bands.byFascia.F1.pct >= Math.round(TIME_BANDS.F1.share * 100)
								? 'Il carico è più concentrato nelle ore di sole della media: buona premessa per la condivisione, purché ci sia produzione a coprirlo.'
								: "Il carico è più spostato fuori dalle ore di sole della media: c'è margine per anticipare consumi in F1 e aumentare l'energia condivisa."}
						</AiBox>
					) : null}
					<Footnote>
						{bands.measured
							? '* Ogni ora misurata è classificata sul calendario TIAD: feriali, sabato, domeniche e festivi nazionali, con ora legale.'
							: '* Distribuzione stimata con profili statistici TERNA 2024 - Delibera ARERA 727/2022.'}
					</Footnote>
				</PanelCard>

				<PanelCard
					title="Top 5 POD per Consumo"
					aside={
						<Row gap="sm">
							<CalcTrace data={tracePerPod} />
						</Row>
					}
				>
					{!hasMeterData ? (
						// Anteprima della tabella (intestazioni + una riga sola col
						// messaggio, non un riquadro tratteggiato separato sotto): dice
						// cosa comparirà appena arriva la prima misura, senza il vuoto
						// sproporzionato di uno `StatusBlock` ad altezza fissa.
						<DataTable
							columns={podColumns}
							rows={[]}
							rowKey={(m) => m.meterId}
							emptyMessage={
								activeMembers.length === 0
									? 'Nessun socio attivo - aggiungi soci nella sezione Soci'
									: 'Nessun consumo per misuratore nel mese di riferimento. La classifica per POD richiede la misura dai misuratori SII/GSE.'
							}
						/>
					) : (
						<>
							<DataTable
								columns={podColumns}
								rows={meterConsumption.slice(0, 5)}
								rowKey={(m) => m.meterId}
								rowActionLabel="Apri i quattro livelli energetici di questo POD"
								onRowClick={(m) =>
									onExplodePod?.({
										meterId: m.meterId,
										puntoMisura: m.codice,
										pod: m.codice,
										direzione: 'Prelievo (M1)',
									})
								}
							/>
							<Footnote>
								* Somme per misuratore dalla serie riconciliata (GSE &gt; bolletta &gt; letture &gt;
								stima).
							</Footnote>
						</>
					)}
				</PanelCard>
			</CardGrid>
		</div>
	);
}
